import { createContext, useContext, useMemo, useCallback } from 'react';
import useSWR, { mutate } from 'swr';
import { axiosService } from 'shared/helpers/axios';
import { useUserContext } from './UserContext';

const FollowCacheContext = createContext();

export function FollowCacheProvider({ children }) {
  const { isAuthenticated } = useUserContext();

  const { data: followCache, isLoading: followCacheLoading } = useSWR(
    isAuthenticated ? '/interact/follows/following/?all=true' : null,
    async () => {
      try {
        const res = await axiosService.get('/interact/follows/following/', {
          params: { page: 1, page_size: 1000 },
        });
        const follows = res.data?.results || res.data || [];
        // Normalize content_type to lowercase for consistent lookups
        return new Set(follows.map((f) => `${f.content_type.toLowerCase()}:${f.object_id}`));
      } catch (err) {
        console.error('Error fetching follow cache:', err);
        return new Set();
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000,
      shouldRetryOnError: false,
    }
  );

  const isFollowing = useCallback(
    (contentType, objectId) => {
      if (!followCache) return false;
      // Ensure contentType is normalized to lowercase
      const normalizedContentType = contentType?.toString().toLowerCase();
      return followCache.has(`${normalizedContentType}:${objectId}`);
    },
    [followCache]
  );

  const updateFollowCache = useCallback(
    (contentType, objectId, following) => {
      const cacheKey = '/interact/follows/following/?all=true';
      mutate(
        cacheKey,
        (currentCache) => {
          if (!currentCache) return currentCache;
          const nextCache = new Set(currentCache);
          // Ensure contentType is normalized to lowercase
          const normalizedContentType = contentType?.toString().toLowerCase();
          const key = `${normalizedContentType}:${objectId}`;
          if (following) {
            nextCache.add(key);
          } else {
            nextCache.delete(key);
          }
          return nextCache;
        },
        false
      );
    },
    []
  );

  const contextValue = useMemo(
    () => ({ followCache, followCacheLoading, isFollowing, updateFollowCache }),
    [followCache, followCacheLoading, isFollowing, updateFollowCache]
  );

  return (
    <FollowCacheContext.Provider value={contextValue}>
      {children}
    </FollowCacheContext.Provider>
  );
}

export function useFollowCache() {
  const context = useContext(FollowCacheContext);
  if (!context) {
    throw new Error('useFollowCache must be used within FollowCacheProvider');
  }
  return context;
}
